import { useComputed } from "@suse-o11y/signal";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { z } from "zod";

import { Collapsible } from "~/components/collapsible";
import { CollapsibleContent } from "~/components/collapsible/content";
import { CollapsibleTrigger } from "~/components/collapsible/trigger";
import { SkeletonBox } from "~/components/skeleton/box";
import { cx } from "~/tools/class-variance";
import { LocalStorage, MAIN_MENU_TERTIARY_ENTRIES_EXPANDED } from "~/tools/local-storage";
import { prop } from "~/tools/prop";

import { closeMainMenu } from "../../app/state/layout/LayoutActions";
import { selectMainMenuOpen } from "../../app/state/layout/LayoutSelectors";
import { ApplicationDomainSwitchLink } from "../../components/application-domain-switch-link";
import { useQueryMainMenuGroups } from "../../queries/mainmenu";
import { useQuery } from "../../queries/react-query";
import { queryServerInfo } from "../../queries/server";
import { queryUIConfig } from "../../queries/ui-config";
import { useDashboardsPermissions } from "../../routes/dashboards/permissions";
import { useAiAssistantAvailability } from "../../sessions/use-ai-assistant-availability";

import { DashboardMenu } from "./dashboardMenu/DashboardMenu";
import { MainMenuExtension } from "./extension/MainMenuExtension";
import { ViewMenu } from "./viewMenu/ViewMenu";
import { MainMenuAccordionButton } from "./MainMenuAccordionButton";
import { MainMenuHeader } from "./MainMenuHeader";
import { MainMenuItemAnalytics } from "./MainMenuItemAnalytics";
import { MainMenuItemCLIProfile } from "./MainMenuItemCLIProfile";
import { MainMenuItemHelp } from "./MainMenuItemHelp";
import { MainMenuItemMetrics } from "./MainMenuItemMetrics";
import { MainMenuItemMonitors } from "./MainMenuItemMonitors";
import { MainMenuItemNotifications } from "./MainMenuItemNotifications";
import { MainMenuItemSessions } from "./MainMenuItemSessions";
import { MainMenuItemSettings } from "./MainMenuItemSettings";
import { StackPacksMenu } from "./MainMenuItemStackPacks";
import { MainMenuItemTraces } from "./MainMenuItemTraces";
import { MainMenuLayout } from "./MainMenuLayout";

export const MainMenu: React.FC = () => {
	const dispatch = useDispatch();
	const open = useSelector(selectMainMenuOpen);
	const { pathname, search } = useLocation();
	const { data: serverInfo } = useQuery(queryServerInfo);
	const enableAi = useAiAssistantAvailability();
	const { data: featureFlags } = useQuery({
		...queryUIConfig,
		select: (config) => ({
			dashboardsEnabled: config.webUIConfig.featureFlags.dashboards,
		}),
	});
	const { dashboardsEnabled = false } = featureFlags ?? {};
	const hasGetDashboardsPermission = useDashboardsPermissions()
		.transform(prop("hasGetDashboardsPermission"))
		.getOrElse(false);

	const { data: extensionGroups, isLoading: extensionIsLoading } = useQueryMainMenuGroups();

	const closeMenu = () => {
		dispatch(closeMainMenu());
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies(pathname): should trigger the effect on change
	// biome-ignore lint/correctness/useExhaustiveDependencies(search): should trigger the effect on change
	React.useEffect(() => {
		// the `open` flag lags a bit due to the animation
		// so we cannot call the dispatch inside `if (open)` condition
		// instead we call it every time when the pathname or search changes
		dispatch(closeMainMenu());
	}, [dispatch, pathname, search]);

	return (
		<MainMenuLayout
			content={
				<div className="relative flex h-full flex-col justify-between" data-testid="main-menu-content">
					<div className="flex-1 space-y-5 overflow-y-scroll py-6.5">
						<MainMenuModeSwitch />
						{extensionGroups?.map((group) => (
							<MainMenuExtension group={group} key={group.name} />
						))}
						{dashboardsEnabled && hasGetDashboardsPermission && <DashboardMenu />}
						<ViewMenu />
					</div>

					<div className="border-grey-300 border-t shadow-overlay-above">
						<div className="py-4.5">
							<MainMenuItemMonitors />
							{enableAi && <MainMenuItemSessions />}
							<MainMenuItemNotifications />
							<MainMenuItemMetrics />
							<MainMenuItemTraces />
						</div>
						<div className="relative min-h-18 bg-grey-100 py-4.5">
							<TertiaryMenu platformVersion={serverInfo?.platformVersion} />
						</div>
					</div>
				</div>
			}
			header={<MainMenuHeader onClose={closeMenu} />}
			onClose={closeMenu}
			open={open && !extensionIsLoading}
		/>
	);
};

interface TertiaryMenuProps {
	platformVersion?: string;
}

const TertiaryMenu: React.FC<TertiaryMenuProps> = ({ platformVersion }) => {
	const tertiaryEntriesExpanded = useComputed(
		(monitor) => LocalStorage.getItem(monitor, MAIN_MENU_TERTIARY_ENTRIES_EXPANDED, z.boolean()) ?? true,
		[],
	);

	return (
		<Collapsible
			onOpenChange={(open) => LocalStorage.setItem(MAIN_MENU_TERTIARY_ENTRIES_EXPANDED, open)}
			open={tertiaryEntriesExpanded}
		>
			<div className={cx("absolute px-6 py-1.5 transition-opacity", tertiaryEntriesExpanded && "opacity-0")}>
				<span className="text-grey-600 text-sm/6">StackPacks, Help and more...</span>
			</div>
			<CollapsibleTrigger asChild>
				<div className="absolute top-0 right-6 -translate-y-1/2">
					<MainMenuAccordionButton
						aria-label={tertiaryEntriesExpanded ? "Close tertiary menu" : "Open tertiary menu"}
						className="outline-2 outline-solid outline-white"
						open={tertiaryEntriesExpanded}
					/>
				</div>
			</CollapsibleTrigger>
			<CollapsibleContent className="w-full">
				<MainMenuItemAnalytics />
				<MainMenuItemCLIProfile />
				<StackPacksMenu />
				<MainMenuItemSettings />
				<div className="relative">
					<MainMenuItemHelp />
					{platformVersion && (
						<div
							className="absolute right-6 bottom-1.5 text-grey-500 text-xs leading-6"
							data-testid="main-menu-version"
						>
							v{platformVersion}
						</div>
					)}
				</div>
			</CollapsibleContent>
		</Collapsible>
	);
};

const MainMenuModeSwitch: React.FC = () => {
	const { data } = useQuery(queryUIConfig);
	const configuredApplicationDomains = data?.applicationDomains;

	return (
		<SkeletonBox
			className={cx(
				"h-7 w-full px-6",
				configuredApplicationDomains != null && configuredApplicationDomains.length < 2 && "hidden",
			)}
			when={configuredApplicationDomains}
		>
			{(loadedConfiguredApplicationDomains) =>
				loadedConfiguredApplicationDomains.length > 1 && (
					<div className="radio-button-group size-full text-13" role="radiogroup">
						{loadedConfiguredApplicationDomains.map((applicationDomain, i) => (
							<ApplicationDomainSwitchLink
								applicationDomain={applicationDomain}
								className="radio-button radio-button-md mode-switch-option-selected:radio-button-selected flex flex-1 mode-switch-option-selected:cursor-default! items-center justify-center gap-1 mode-switch-option-icon:stroke-[1.5px] mode-switch-option-icon:text-15"
								isDefault={i === 0}
								key={applicationDomain}
								role="radio"
							/>
						))}
					</div>
				)
			}
		</SkeletonBox>
	);
};
